import { memo, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { NetworkStatus } from "@apollo/client";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

import { ModelType } from "../../../../API";
import { IDateRangeVariable } from "../../../../common/components/datePicker/modelsDatePickers";
import InfiniteScrollWrapper from "../../../../common/components/infinite-scroll/InfiniteScrollWrapper";
import FullPageLoader from "../../../../common/components/item/FullPageLoader";
import { MediaFullScreen } from "../../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../../common/components/table/TableCellTruncated";
import TableNoDataOverlay from "../../../../common/components/table/TableNoDataOverlay";
import { useHasRoleAccess } from "../../../../common/hooks/useCheckRoleAccess";
import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import { AutocompleteOptionType } from "../../../../common/models/autocomplete";
import { RoleEnum, RouteEnum } from "../../../../common/models/enums";
import { UpdateEventFeedbackInput } from "../../../../common/types/events/api";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import {
  DEFAULT_MEDIA_ICON_STYLES,
  modelToServiceMap,
} from "../../../../common/variables/common";
import { useSelectedZone } from "../../../../common/variables/selectedZone";
import { useSelectedStatus } from "../../../../common/variables/selectedStatus";
import DraggedModalBody from "../../../devices/device-details/components/model-settings/DraggedModalBody";
import { useConfirm } from "material-ui-confirm";
import confirmDialogStyleOptions from "../../../../common/helpers/confirmDialogParams";
import LiveViewButtonContainer from "../../../telemetry/components/LiveViewButtonContainer";
import { useGetRecentEvents } from "../../hooks/events/useGetRecentEvents";
import { useUpdateEventFeedback } from "../../hooks/events/useUpdateEventFeedback";
import { useFeedbackUpdateConfirmDialog } from "../../hooks/useFeedbackUpdateConfirmDialog";
import { EventWithRecordingViewModel } from "../../models/eventWithRecordingViewModel";
import FeedbackThumbsTableCellContainer from "../feedback/FeedbackThumbsTableCellContainer";
import BasicEventTable from "./BasicEventTable";
import EventModalContent from "./EventModalContent";
import { getEventStatusInfo } from "../../utils/eventStatusUtils";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface EventTableContainerProps {
  customerId: string;
  facilityId: AutocompleteOptionType;
  modelId: AutocompleteOptionType;
  cameraId?: string;
  dateRange?: IDateRangeVariable | null;
}

const EventTableContainer = ({
  customerId,
  modelId,
  facilityId,
  cameraId,
  dateRange,
}: EventTableContainerProps): JSX.Element => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const { getCachedLocationName } = useLocationFromCache();
  const isVerifier = useHasRoleAccess(RoleEnum.VERIFIER);
  const isHardHatPage =
    pathname.includes(RouteEnum.TelemetryHardHat) ||
    pathname.includes(RouteEnum.SensoryTelemetryHardHat);

  const {
    events,
    loading,
    fetchNextPage,
    lastEvaluatedKey,
    fetchMoreLoading,
    networkStatus,
  } = useGetRecentEvents({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
    deviceId: cameraId ?? "",
    fromDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
    toDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
  });

  // Prepare feedback updater with the same variables used for fetching, so cache updates remain consistent
  const { updateEventFeedback, error } = useUpdateEventFeedback({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
    deviceId: cameraId ?? "",
    fromDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
    toDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
  });
  const handleFeedbackUpdate = useFeedbackUpdateConfirmDialog(
    updateEventFeedback,
    error
  );

  const selectedZone = useSelectedZone();
  const selectedStatus = useSelectedStatus();

  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [infoDirty, setInfoDirty] = useState(false);
  const confirm = useConfirm();

  const handleRowOpen = (eventVm: EventWithRecordingViewModel) => {
    setSelectedEventId(eventVm.event.id);

    setOpenEventDialog(true);
  };

  const handleRowClose = async () => {
    if (infoDirty) {
      try {
        await confirm({
          title: "Discard your changes?",
          description:
            "You have unsaved changes. If you close now, your changes will be lost.",
          confirmationText: "Discard",
          cancellationText: "Cancel",
          ...confirmDialogStyleOptions,
        });

        setOpenEventDialog(false);

        setInfoDirty(false);
      } catch {
        // user cancelled close
      }
    } else {
      setOpenEventDialog(false);
    }
  };

  const memoizedEvents = useMemo(() => {
    const normalizeDeviceId = (id?: string): string => {
      if (!id) return "";

      return id.startsWith("DE#") ? id.slice(3) : id;
    };

    const baseEvents = isHardHatPage
      ? events
      : events.filter(
          item => item.event.modelId !== ModelType.hardhat.toUpperCase()
        );

    let filtered = baseEvents;

    const selectedDeviceId = cameraId ?? "";

    if (selectedDeviceId) {
      const normalizedSelected = normalizeDeviceId(selectedDeviceId);

      filtered = filtered.filter(({ event, newestRecording }) => {
        const fromEvent = normalizeDeviceId(event.camera_id ?? "");
        const fromRecording = normalizeDeviceId(
          // some records have camera_id in the raw event info nested under data
          // fallback to instance_name if camera_id is missing
          (newestRecording?.rawEventInfo as any)?.data?.camera_id ??
            (newestRecording?.rawEventInfo as any)?.data?.instance_name ??
            ""
        );

        return (
          fromEvent === normalizedSelected ||
          fromRecording === normalizedSelected
        );
      });
    }

    // Filter by selected zone
    if (selectedZone?.value) {
      filtered = filtered.filter(({ newestRecording }) => {
        const zoneId =
          (newestRecording?.rawEventInfo as any)?.data?.zone_id ?? "";

        return zoneId === selectedZone.value;
      });
    }

    // Filter by selected status
    if (selectedStatus?.value) {
      filtered = filtered.filter(({ event, newestRecording }) => {
        const { status } = getEventStatusInfo(
          event.timestamp,
          newestRecording?.timestamp ?? event.timestamp,
          event.recordingsCount ?? 1,
          event.modelId
        );

        return status === selectedStatus.value;
      });
    }

    return filtered;
  }, [events, pathname, cameraId, selectedZone, selectedStatus]);

  const selectedEvent = useMemo(
    () => memoizedEvents.find(e => e.event.id === selectedEventId) ?? null,
    [memoizedEvents, selectedEventId]
  );

  if (loading) return <FullPageLoader />;

  if (!memoizedEvents.length && !loading) {
    return <TableNoDataOverlay />;
  }

  // On hard hat pages, render the original split tables with expandable cards
  if (isHardHatPage) {
    return (
      <Box display="flex" flexDirection="column" gap="12px">
        <InfiniteScrollWrapper
          fetchNextPage={fetchNextPage}
          lastEvaluatedKey={lastEvaluatedKey}
          loading={loading || fetchMoreLoading}
        >
          {memoizedEvents.map(({ event, newestRecording }) => (
            <BasicEventTable
              event={event}
              newestRecording={newestRecording}
              key={event.id}
            />
          ))}
        </InfiniteScrollWrapper>
        {fetchMoreLoading && networkStatus !== NetworkStatus.error && (
          <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Box>
        )}
      </Box>
    );
  }

  // Control room (non-hard hat) pages use the grouped single table
  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <InfiniteScrollWrapper
        fetchNextPage={fetchNextPage}
        lastEvaluatedKey={lastEvaluatedKey}
        loading={loading || fetchMoreLoading}
      >
        <TableContainer
          component={Box}
          sx={{
            borderRadius: "0.625rem",
            border: `1px solid ${theme.palette.otherOutlineBorder.main}`,
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="recent events table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.otherOutlineBorder.main,
                    textTransform: "uppercase",
                    width: "350px",
                    padding: "12px 16px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Event
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.otherOutlineBorder.main,
                    textTransform: "uppercase",
                    width: "160px",
                    padding: "12px 16px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Location
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.otherOutlineBorder.main,
                    textTransform: "uppercase",
                    width: "160px",
                    padding: "12px 16px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Zone
                  </Typography>
                </TableCell>
                {!isVerifier && (
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.otherOutlineBorder.main,
                      textTransform: "uppercase",
                      width: "140px",
                      padding: "12px 16px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Feedback
                    </Typography>
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.otherOutlineBorder.main,
                    textTransform: "uppercase",
                    width: "120px",
                    padding: "12px 16px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Action
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memoizedEvents.map(({ event, newestRecording }) => {
                const locationName = getCachedLocationName(event.location);
                const zoneId =
                  (newestRecording?.rawEventInfo as any)?.data?.zone_id ?? "";
                const { status, duration } = getEventStatusInfo(
                  event.timestamp,
                  newestRecording?.timestamp ?? event.timestamp,
                  event.recordingsCount ?? 1,
                  event.modelId
                );

                const onUpdateEventFeedback = async (
                  input: UpdateEventFeedbackInput
                ) => {
                  await handleFeedbackUpdate(input, event);
                };

                return (
                  <TableRow key={event.id}>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                        backgroundColor:
                          theme.palette.otherBackgroundLight.main,
                        borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                        width: "350px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          width: "350px",
                          alignItems: "center",
                          gap: "0.75em",
                        }}
                      >
                        <MediaFullScreen
                          keyFrame={newestRecording?.keyFrame}
                          mediaOutput={newestRecording?.mediaOutput}
                          s3ImageFullScreenProps={{
                            sx: DEFAULT_MEDIA_ICON_STYLES,
                            fixedHeight: "75px",
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Typography variant="buttonMedium">
                              {
                                modelToServiceMap[
                                  event.modelId.toLowerCase() as ModelType
                                ]
                              }
                            </Typography>
                            {/* Clock icon + Duration/Status */}
                            {duration && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  maxWidth: "fit-content",
                                }}
                              >
                                <AccessTimeIcon
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2Regular"
                                  color="text.secondary"
                                >
                                  {duration}
                                </Typography>
                              </Box>
                            )}
                            <Typography
                              variant="body2Regular"
                              color="text.secondary"
                            >
                              {status}
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2Regular"
                            color="text.secondary"
                          >
                            {formatTimestamp(event.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                        backgroundColor:
                          theme.palette.otherBackgroundLight.main,
                        borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                        width: "160px",
                      }}
                    >
                      <TableCellTruncated
                        tooltipText={locationName}
                        text={locationName}
                        TypographyProps={{ variant: "body2" }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                        backgroundColor:
                          theme.palette.otherBackgroundLight.main,
                        borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                        width: "160px",
                      }}
                    >
                      <TableCellTruncated
                        tooltipText={zoneId}
                        text={zoneId}
                        TypographyProps={{ variant: "body2" }}
                      />
                    </TableCell>
                    {!isVerifier && (
                      <TableCell
                        sx={{
                          padding: "12px 16px",
                          backgroundColor:
                            theme.palette.otherBackgroundLight.main,
                          borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                          width: "140px",
                        }}
                      >
                        <FeedbackThumbsTableCellContainer
                          event={event}
                          onUpdateEventFeedback={onUpdateEventFeedback}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                        backgroundColor:
                          theme.palette.otherBackgroundLight.main,
                        borderBottom: `1px solid ${theme.palette.otherOutlineBorder.main}`,
                        width: "120px",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LiveViewButtonContainer
                          cameraId={cameraId ?? ""}
                          nodeId={event.nodeId ?? ""}
                        />
                        <Button
                          sx={{ cursor: "pointer" }}
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handleRowOpen({ event, newestRecording })
                          }
                        >
                          <Typography variant="buttonMedium">
                            Details
                          </Typography>
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScrollWrapper>
      {fetchMoreLoading && networkStatus !== NetworkStatus.error && (
        <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      )}
      <DraggedModalBody
        title="Event Details"
        open={openEventDialog}
        persistId="event-details"
        width={"90vw"}
        height={"85vh"}
        onClose={handleRowClose}
      >
        {selectedEvent && (
          <EventModalContent
            event={selectedEvent.event}
            newestRecording={selectedEvent.newestRecording}
            onUpdateEventFeedback={handleFeedbackUpdate}
            onInfoDirtyChange={setInfoDirty}
          />
        )}
      </DraggedModalBody>
    </Box>
  );
};

export default memo(EventTableContainer);
